/**
 * Arabic.
 *
 * The most demanding locale in the QA set, and the reason it was translated
 * first: Arabic is RTL *and* cursive, so glyphs join and change shape by
 * position. Bugs that Hebrew hides — a font that has no Arabic coverage, text
 * that is reordered rather than reshaped, a layout that assumes fixed glyph
 * widths — only show up here.
 *
 * Arabic has six plural categories (zero, one, two, few, many, other). Any
 * message with a count has to spell out the ones it uses; the formatter picks
 * between them with Intl.PluralRules.
 *
 * **Needs review by a native speaker before shipping.** The terminology around
 * encryption is the risky part: "safety number", "end-to-end", "identity key"
 * are terms of art, and a plausible-sounding wrong choice is worse here than an
 * obvious gap, because a user cannot tell that a security control has been
 * mislabelled.
 */
import type { MessageKey } from './en.ts';

export const ar: Partial<Record<MessageKey, string>> = {
  'app.opening': 'جارٍ فتح الخزنة…',
  'app.empty.title': 'اختر محادثة',
  'app.empty.body':
    'رسائلك مشفّرة من طرف إلى طرف. يمرّر الخادم بينكما مظاريف مُغلقة — لا يستطيع فتحها، ولا يستطيع ذلك أي شخص يصل إليه.',
  'app.identityAlert.title': 'تغيّر مفتاح الهوية',
  'app.identityAlert.body':
    'تغيّر مفتاح الهوية الخاص بـ {name}. ربما أعادوا تثبيت التطبيق — أو ربما ينتحل أحدهم شخصيتهم.',
  'app.identityAlert.accept': 'قبول المفتاح الجديد',
  'app.identityAlert.dismiss': 'تجاهل',

  'onboarding.invited': 'دعاك {name}.',
  'onboarding.invited.detail': 'بعد التسجيل، ستكون محادثتك معهم بانتظارك.',
  'onboarding.intro':
    'رسائل مشفّرة من طرف إلى طرف. تُنشأ مفاتيحك هنا، على جهازك، ولا تغادره أبدًا.',
  'onboarding.username.label': 'اسم المستخدم',
  'onboarding.username.hint': '٣–٣٢ حرفًا، بنظام كتابة واحد.',
  'onboarding.displayName.label': 'الاسم الظاهر',
  'onboarding.phone.label': 'رقم الهاتف — اختياري',
  'onboarding.phone.hint':
    'فقط ليعثر عليك من لديه رقمك بالفعل. ليس اسم حسابك، وليس وسيلة تسجيل الدخول، وإزالته لاحقًا لا تأخذ معها أي شيء آخر.',
  'onboarding.submit': 'إنشاء حساب',
  'onboarding.submit.busy': 'جارٍ إنشاء المفاتيح…',
  'onboarding.warning':
    'لا توجد كلمة مرور ولا استرداد. هويتك مفتاح خاص محفوظ في هذا المتصفح — إذا مسحت بيانات الموقع، فستحتاج إلى التسجيل من جديد.',
  'onboarding.username.error.too_short': '٣ أحرف على الأقل.',
  'onboarding.username.error.too_long': '٣٢ حرفًا كحدّ أقصى.',
  'onboarding.username.error.bad_start': 'لا يمكن أن يبدأ بنقطة أو شرطة أو شرطة سفلية.',
  'onboarding.username.error.disallowed_character': 'يحتوي على حرف غير مسموح به.',
  'onboarding.username.error.mixed_script':
    'يخلط بين نظامَي كتابة. اختر واحدًا، حتى لا يستطيع أحد تقليد اسمك.',
  'onboarding.username.error.reserved': 'هذا الاسم محجوز.',

  'status.online': 'متصل',
  'status.connecting': 'جارٍ الاتصال…',
  'status.offline': 'غير متصل',
  'sidebar.invite': 'دعوة أصدقاء',
  'sidebar.findByPhone': 'البحث برقم الهاتف',
  'sidebar.newChat': 'محادثة جديدة',
  'sidebar.search': 'البحث في المحادثات',
  'sidebar.conversations': 'المحادثات',
  'sidebar.empty': 'لا توجد محادثات بعد.',
  'sidebar.noMatches': 'لا توجد نتائج',
  'sidebar.noMessages': 'لا توجد رسائل بعد',
  'sidebar.group': 'مجموعة',
  'sidebar.notifications.on': 'الإشعارات مفعّلة',
  'sidebar.notifications.denied': 'الإشعارات محظورة في المتصفح',
  'sidebar.notifications.enable': 'تفعيل الإشعارات',

  'thread.back': 'العودة إلى المحادثات',
  'thread.typing':
    '{count, plural, one {{names} يكتب…} two {{names} يكتبان…} few {{names} يكتبون…} other {{names} يكتبون…}}',
  'thread.members':
    '{count, plural, zero {لا أعضاء} one {عضو واحد} two {عضوان} few {# أعضاء} many {# عضوًا} other {# عضو}}',
  'thread.verifySafety': 'التحقق من رقم الأمان',
  'thread.encrypted':
    'الرسائل في هذه المحادثة مشفّرة من طرف إلى طرف. حتى الخادم لا يستطيع قراءتها.',
  'thread.replyingTo': 'ردًّا على: {preview}',
  'thread.attachment': 'مرفق',
  'thread.cancelReply': 'إلغاء الرد',
  'thread.recording': 'جارٍ التسجيل… أفلت للإرسال',
  'thread.cancelRecording': 'إلغاء التسجيل',
  'thread.composer': 'رسالة',
  'thread.send': 'إرسال',
  'thread.voice.unavailable.title':
    'التسجيل الصوتي يتطلب HTTPS — يحجب المتصفح الميكروفون على هذا العنوان',
  'thread.voice.unavailable.label': 'التسجيل الصوتي غير متاح بدون HTTPS',
  'thread.voice.start': 'تسجيل رسالة صوتية',
  'thread.voice.stop': 'إيقاف وإرسال',
  'thread.voice.error.title': 'تعذّر التسجيل',

  'bubble.deleted': 'حُذفت هذه الرسالة',
  'bubble.actions': 'إجراءات الرسالة',
  'bubble.reply': 'رد',
  'profile.profiles': 'الملفات الشخصية',
  'profile.profiles.explain': 'حسابات منفصلة على هذا الجهاز — للعمل، وللشخصي، ولنشاط تجاري. تبقى كلها متصلة، فتصلك إشعارات أي منها.',
  'profile.profiles.add': 'إضافة ملف شخصي',
  'profile.profiles.create': 'إنشاء',
  'profile.profiles.namePlaceholder': 'العمل، شخصي…',
  'profile.profiles.unnamed': 'ملف بلا اسم',
  'profile.profiles.remove': 'حذف الملف الشخصي',
  'profile.profiles.removeConfirm': 'حذف هذا الحساب نهائيًا؟',
  'profile.profiles.warning': 'الملف الجديد حساب جديد باسم مستخدم خاص به — وليس عرضًا آخر لهذا الحساب. ولا يمكن استرداده أيضًا.',
  'profile.build': 'إصدار {build}',
  'profile.name.edit': 'تغيير الاسم',
  'profile.name.save': 'حفظ',
  'profile.name.cancel': 'إلغاء',
  'profile.name.failed': 'تعذّر تغيير الاسم. تحقّق من الاتصال وأعد المحاولة.',
  'profile.delete': 'حذف الحساب',
  'profile.delete.explain': 'يحذف هذا حسابك من الخادم نهائيًا. مفاتيحك موجودة هنا فقط، فلا سبيل للعودة ولا لاسترداد أي شيء.',
  'profile.delete.others': 'الرسائل التي أرسلتها تبقى على الأجهزة التي استلمتها. لا يُبلَّغ أحد بمغادرتك — سيكتشفون ذلك إن كتبوا إليك.',
  'profile.delete.confirmPrompt': 'اكتب اسم المستخدم للتأكيد',
  'profile.delete.confirm': 'حذف نهائيًا',
  'profile.delete.failed': 'تعذّر حذف الحساب. تحقّق من الاتصال وأعد المحاولة.',
  'thread.recipientGone': 'لم يعد هذا الشخص على VAV. لن تصله رسائلك.',
  'thread.recipientGone.title': 'هذا الحساب حُذف',
  'profile.avatar.change': 'تغيير الصورة',
  'profile.avatar.remove': 'إزالة الصورة',
  'profile.avatar.explain': 'صورتك مشفّرة وتُرسل فقط لمن تتحدث معهم. الغرباء يرون الحروف الأولى من اسمك.',
  'avatar.crop.title': 'ضبط الصورة',
  'avatar.crop.hint': 'اسحب لتحديد الموضع، وحرّك المؤشر للتكبير. ما داخل الدائرة هو ما يراه الجميع.',
  'avatar.crop.zoom': 'تكبير',
  'avatar.crop.loadFailed': 'تعذّرت قراءة هذه الصورة. قد تكون بتنسيق لا يدعمه التطبيق — جرّب صورة أخرى.',
  'call.incoming.audio': 'مكالمة صوتية واردة',
  'call.incoming.video': 'مكالمة فيديو واردة',
  'call.accept': 'رد',
  'call.decline': 'رفض',
  'call.outgoing': 'يرن…',
  'call.cancel': 'إلغاء',
  'call.hangup': 'إنهاء المكالمة',
  'call.mute': 'كتم',
  'call.unmute': 'إلغاء الكتم',
  'call.cameraOff': 'إيقاف الكاميرا',
  'call.cameraOn': 'تشغيل الكاميرا',
  'call.connecting': 'جارٍ الاتصال…',
  'call.ended.declined': 'رُفضت المكالمة',
  'call.ended.busy': 'مشغول',
  'call.ended.hangup': 'انتهت المكالمة',
  'call.ended.failed': 'تعذّر بدء المكالمة',
  'call.ended.unanswered': 'لا يوجد رد',
  'thread.call.audio': 'مكالمة صوتية',
  'thread.call.video': 'مكالمة فيديو',
  'call.legFailed': 'تعذّر الاتصال',
  'call.turnMissing': 'خادم الترحيل (TURN) غير مُعدّ بعد — المكالمات بين شبكات مختلفة تحتاجه',
  'call.forProfile': 'إلى ملفك الشخصي {name}',
  'call.toSpeaker': 'مكبّر الصوت',
  'call.toEarpiece': 'سماعة الأذن',
  'onboarding.link.start': 'لديّ حساب بالفعل — اربط هذا الجهاز',
  'onboarding.link.title': 'ربط هذا الجهاز',
  'onboarding.link.hint': 'على جهاز فيه حسابك بالفعل: تفاصيلي، ربط جهاز جديد، ثم امسح هذا الرمز أو الصقه.',
  'onboarding.link.verifyHint': 'تأكد من ظهور هذه الأرقام على الجهاز الموافق أيضًا.',
  'onboarding.link.copy': 'نسخ رمز الربط',
  'onboarding.link.copied': 'تم النسخ ✓',
  'onboarding.link.waiting': 'في انتظار الموافقة…',
  'onboarding.link.failed': 'لم يكتمل الربط. ابدأ من جديد على الجهازين.',
  'profile.device.remove': 'فصل',
  'profile.logout': 'تسجيل الخروج من هذا الجهاز',
  'profile.logout.explain': 'يبقى حسابك على أجهزتك الأخرى. كل ما هو محفوظ على هذا الجهاز سيُمحى منه.',
  'profile.logout.confirm': 'تسجيل الخروج',
  'link.approve.stranded': 'هذا المتصفح لا يحتوي على حسابك، لذا لا يمكنه الموافقة على الجهاز الجديد. افتح الرابط (أو الصق الرمز) في تطبيق VAV على الجهاز الذي يوجد فيه حسابك.',
  'link.approve.title': 'الموافقة على جهاز جديد',
  'link.approve.account': 'سينضم الجهاز الجديد إلى الحساب {name}.',
  'profile.link.start': 'ربط جهاز جديد',
  'profile.link.hint': 'الصق رمز الربط الظاهر على الجهاز الجديد.',
  'profile.link.verifyHint': 'وافق فقط إذا ظهرت الأرقام نفسها على الجهاز الجديد.',
  'profile.link.approve': 'الموافقة على الجهاز',
  'profile.link.done': 'تم ربط الجهاز',
  'profile.link.failed': 'تعذّرت الموافقة — ربما انتهت صلاحية الرمز.',
  'thread.date.today': 'اليوم',
  'thread.date.yesterday': 'أمس',
  'contact.delete': 'حذف جهة الاتصال',
  'contact.delete.explain': 'ستُحذف المحادثة وسجلّها من هذا الجهاز فقط. لن يُخطَر الطرف الآخر، ونسخته تبقى لديه.',
  'group.leave': 'مغادرة المجموعة',
  'group.leave.explain': 'سيرى الأعضاء أنك غادرت. ستُحذف المجموعة وسجلّها من هذا الجهاز.',
  'contact.title': 'تفاصيل جهة الاتصال',
  'contact.verified': 'مُوثَّق',
  'profile.title': 'بياناتي',
  'profile.username': 'اسم المستخدم',
  'profile.thisDevice': 'هذا الجهاز',
  'profile.devices': 'الأجهزة',
  'profile.devicesLoading': 'جارٍ التحميل…',
  'profile.devicesNone': 'تعذّر الوصول إلى الخادم.',
  'profile.deviceCurrent': 'هذا الجهاز',
  'profile.install': 'التثبيت على هذا الجهاز',
  'profile.install.action': 'إضافة إلى الشاشة الرئيسية',
  'profile.install.already': 'أنت تستخدم التطبيق المثبَّت.',
  'profile.install.ios': 'افتح قائمة المشاركة واختر «إضافة إلى الشاشة الرئيسية».',
  'profile.install.unavailable': 'المتصفح لا يعرض التثبيت الآن — غالبًا لأن التطبيق مثبَّت بالفعل. ابحث عن VAV في قائمة تطبيقاتك.',
  'profile.noRecovery': 'مفاتيحك موجودة على هذا الجهاز فقط. مسح بيانات المتصفح أو فقدان الهاتف يعني فقدان الحساب — لا يوجد استرداد.',
  'bubble.forward': 'إعادة توجيه',
  'bubble.select': 'تحديد',
  'bubble.moreReactions': 'مزيد من التفاعلات',
  'select.cancel': 'إلغاء التحديد',
  'select.count': '{count, plural, =0 {تحديد الرسائل} one {تم تحديد رسالة} two {تم تحديد رسالتين} few {تم تحديد # رسائل} many {تم تحديد # رسالة} other {تم تحديد # رسالة}}',
  'forward.title': 'إعادة التوجيه إلى',
  'forward.send': 'إرسال {count, plural, one {رسالة} two {رسالتين} few {# رسائل} many {# رسالة} other {# رسالة}} إلى {to, plural, one {محادثة} two {محادثتين} few {# محادثات} many {# محادثة} other {# محادثة}}',
  'thread.voice.blocked.how': 'افتح vav.chat في متصفح Chrome (لا في التطبيق المثبَّت)، اضغط الأيقونة يسار شريط العنوان ← الأذونات ← إعادة تعيين الأذونات. ثم اضغط الميكروفون مرة أخرى ووافق.',
  'thread.voice.retry': 'أعد المحاولة',
  'thread.voice.blockedByOs': 'هذا الموقع مسموح له باستخدام الميكروفون، إذًا الحظر ليس هنا — المتصفح نفسه لا يملك إذن الميكروفون من الهاتف.',
  'thread.voice.blockedByOs.how': 'أندرويد: الإعدادات ← التطبيقات ← Chrome (أو VAV إن كنت تستخدم التطبيق المثبَّت) ← الأذونات ← الميكروفون ← السماح.',
  'thread.voice.sendFailed': 'تعذّر إرسال التسجيل. تحقّق من الاتصال وأعد المحاولة.',
  'thread.voice.busy': 'الميكروفون مسموح لكن تعذّر فتحه. على الأرجح يستخدمه تطبيق آخر.',
  'thread.voice.busy.how': 'أغلق أي شيء قد يشغله — مكالمة، مساعد صوتي، مسجّل — ثم أعد المحاولة. إعادة تشغيل الهاتف تحرّر ميكروفونًا عالقًا.',
  'thread.unreadFrom': '{count, plural, one {رسالة جديدة} two {رسالتان جديدتان} few {# رسائل جديدة} many {# رسالة جديدة} other {# رسالة جديدة}}',
  'thread.voice.blocked': 'الميكروفون محظور لهذا الموقع. بعد الرفض لا يسأل المتصفح مرة أخرى — يجب تغيير ذلك من الإعدادات.',
  'thread.voice.noMicrophone': 'لم يُعثر على ميكروفون في هذا الجهاز.',
  'push.prompt.title': 'الإشعارات مُعطَّلة',
  'push.prompt.body': 'فعِّلها وسيخبرك هاتفك عند وصول رسالة، حتى والتطبيق مغلق.',
  'push.prompt.denied': 'هذا الموقع محظور من إرسال الإشعارات. اسمح بها من إعدادات الموقع في المتصفح ثم عُد.',
  'push.prompt.enable': 'تفعيل',
  'push.prompt.dismiss': 'ليس الآن',
  'quote.you': 'أنت',
  'quote.them': 'هم',
  'quote.unavailable': 'الرسالة غير متاحة',
  'quote.image': 'صورة',
  'quote.video': 'فيديو',
  'quote.audio': 'صوت',
  'quote.voice': 'رسالة صوتية',
  'schedule.title': 'إرسال لاحقًا',
  'schedule.explain': 'تُكتب الرسالة وتُختم الآن، وتُرسل في الوقت الذي تختاره. حتى ذلك الحين يمكنك إلغاؤها من قائمة ⋯.',
  'schedule.confirm': 'جدولة',
  'bubble.cancelScheduled': 'إلغاء الإرسال المجدول',
  'bubble.state.scheduled': 'مجدولة',
  'profile.checkUpdate': 'التحقق من التحديث',
  'profile.checkUpdate.busy': 'يتحقق…',
  'profile.checkUpdate.current': 'أنت على أحدث إصدار.',
  'bubble.edit': 'تعديل',
  'bubble.edited': 'مُعدَّلة',
  'thread.editing': 'تعديل الرسالة',
  'group.edit.title': 'تفاصيل المجموعة',
  'group.edit.name': 'اسم المجموعة',
  'bubble.deleteForMe': 'حذف لديّ',
  'bubble.save': 'حفظ الصورة',
  'bubble.closeImage': 'إغلاق الصورة',
  'bubble.deleteForEveryone': 'حذف لدى الجميع',
  'bubble.decryptFailed': 'تعذّر فك تشفير هذا المرفق.',
  'bubble.decrypting': 'جارٍ فك التشفير…',
  'bubble.state.pending': 'قيد الانتظار',
  'bubble.state.sent': 'أُرسلت',
  'bubble.state.delivered': 'وصلت',
  'bubble.state.read': 'قُرئت',
  'bubble.state.failed': 'فشلت',

  'attach.label': 'إرفاق',
  'attach.camera': 'الكاميرا',
  'attach.image': 'صورة',
  'attach.video': 'فيديو',
  'attach.audio': 'صوت',
  'attach.document': 'مستند',
  'attach.error.title': 'تعذّر الإرفاق',

  'media.play': 'تشغيل',
  'media.pause': 'إيقاف مؤقت',
  'media.seek': 'الموضع في التسجيل',
  'media.decrypting': 'جارٍ فك التشفير…',
  'media.permission.library': 'لا يوجد إذن للوصول إلى مكتبة الصور',
  'media.permission.camera': 'لا يوجد إذن للوصول إلى الكاميرا',
  'media.permission.microphone': 'لا يوجد إذن للوصول إلى الميكروفون',

  'invite.title': 'دعوة أصدقاء',
  'invite.close': 'إغلاق',
  'invite.body':
    'أرسل الرابط. سيرى من يفتحه أنك دعوته، وستكون محادثتك معه بانتظاره بمجرد تسجيله.',
  'invite.preparing': 'جارٍ تجهيز رابط…',
  'invite.share': 'مشاركة',
  'invite.copy': 'نسخ الرابط',
  'invite.copied': 'تم النسخ',
  'invite.copyFailed': 'تعذّر النسخ — حدّد الرابط وانسخه يدويًا',
  'invite.caution':
    'الرابط صالح لمدة ٣٠ يومًا ويمكن استخدامه أكثر من مرة. أي شخص يملكه يستطيع التسجيل ورؤية أنك دعوته — لا تنشره علنًا.',
  'invite.message': 'يدعوك {name} إلى VAV — رسائل مشفّرة من طرف إلى طرف.',
  'invite.defaultName': 'صديق',

  'newChat.title': 'محادثة جديدة',
  'newChat.search': 'اسم المستخدم أو الاسم الكامل أو رقم الهاتف',
  'newChat.searchByUsername': 'البحث باسم المستخدم',
  'newChat.direct': 'محادثة',
  'newChat.group': 'مجموعة',
  'newChat.groupName': 'اسم المجموعة',
  'newChat.pickHint': 'اختر المشاركين من النتائج — بالعدد الذي تريده.',
  'newChat.searching': 'جارٍ البحث…',
  'newChat.noResults': 'لم يُعثر على مستخدمين بهذا الاسم.',
  'newChat.createGroup': 'إنشاء مجموعة',
  'newChat.openChat': 'فتح المحادثة',

  'phone.title': 'رقم الهاتف',
  'phone.privacy':
    'يُجزَّأ رقمك على جهازك قبل إرساله. لا يخزّن الخادم سوى البصمة، ولا يخزّن الرقم أبدًا.',
  'phone.mine': 'رقمي — ليتمكن الناس من العثور عليّ',
  'phone.save': 'حفظ',
  'phone.saved': 'تم الحفظ',
  'phone.findFriends': 'البحث عن أصدقاء',
  'phone.fromContacts': 'الاختيار من جهات الاتصال',
  'phone.noContactPicker':
    'لا يسمح هذا المتصفح بالوصول إلى جهات الاتصال. اكتب رقمًا للتحقق، أو استخدم Chrome على أندرويد.',
  'phone.checkOne': 'التحقق من رقم واحد',
  'phone.search': 'بحث',
  'phone.noneRegistered': 'لا يوجد أي من هذه الأرقام مسجّلًا.',
  'phone.invalid': 'لا يبدو هذا رقم هاتف',

  'safety.title': 'رقم الأمان',
  'safety.compare':
    'قارن هذا الرقم مع {name} عبر قناة أخرى — لقاء مباشر أو مكالمة هاتفية. إذا تطابق الرقمان، فلا أحد في المنتصف.',
  'safety.confirm': 'تحققت من هذا الرقم',
  'safety.changeNote':
    'إذا أعاد {name} تثبيت التطبيق، فسيتغيّر الرقم وستصلك إشعار. التغيّر غير المتوقع يستحق التحقق قبل متابعة الإرسال.',
  'safety.noSession': 'لم تُتبادَل أي مفاتيح مع {name} بعد. أرسل رسالة واحدة ثم عُد إلى هنا.',

  'common.cancel': 'إلغاء',
  'push.channel': 'الرسائل',
  'push.newMessage': 'رسالة جديدة',
  'language.title': 'اللغة',
  'language.note': 'تُكتشف من متصفحك. غيّرها هنا إذا كان التخمين خاطئًا.',
  'language.restart': 'أعد تشغيل التطبيق لإكمال تغيير اتجاه التخطيط.',
  'app.failed.title': 'تعذّر البدء',
  'app.failed.retry': 'حاول مرة أخرى',
  'phone.invalidHint': 'جرّب الصيغة الدولية الكاملة، بدءًا من رمز بلدك.',
  'phone.country': 'البلد',
  'phone.national': 'رقمك، كما تطلبه محليًا',
  'phone.willSave': 'سيُحفظ باسم {number}',
  'phone.searchCountry': 'البحث عن بلد',
  'phone.importContacts': 'البحث في جهات اتصالي',
  'phone.importing': 'جارٍ فحص جهات اتصالك…',
  'phone.importingProgress': 'جارٍ فحص جهات اتصالك… {done} من {total}',
  'phone.importScanning': 'جارٍ قراءة جهات اتصالك…',
  'phone.importScanned': '{entries} مدخلاً، {unique, plural, zero {لا أرقام للفحص} one {رقم واحد للفحص} two {رقمان للفحص} few {# أرقام للفحص} many {# رقمًا للفحص} other {# رقم للفحص}}',
  'phone.contactsDenied': 'رُفض إذن قراءة جهات الاتصال. ما زال بإمكانك التحقق من رقم يدويًا.',
  'phone.matched': '{count, plural, zero {لا أحد من جهات اتصالك هنا بعد} one {جهة اتصال واحدة هنا} two {جهتا اتصال هنا} few {# جهات اتصال هنا} many {# جهة اتصال هنا} other {# جهة اتصال هنا}}',
  'phone.contactsPrivacy': 'تُجزَّأ جهات اتصالك على هذا الجهاز. لا يُرفع دفتر العناوين نفسه أبدًا.',
  'invite.share.subject': 'انضم إليّ على VAV',
};
